import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useData } from '../DataContext';

export default function RentalsPage() {
  const {
    rentals,
    setRentals,
    customers,
    cars,
  } = useData();

  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedRental, setEditedRental] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [totalInput, setTotalInput] = useState('');
  const [selectedCarId, setSelectedCarId] = useState(''); // Для отслеживания выбранной машины

  const token = localStorage.getItem('token');
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL + '/api',
    headers: { Authorization: `Bearer ${token}` },
  });

  // 🕐 Функции для работы с часовыми поясами
  function toLocalDatetimeString(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  function toISOStringLocal(datetimeLocalValue) {
    if (!datetimeLocalValue) return null;
    const date = new Date(datetimeLocalValue);
    return date.toISOString();
  }

  // 📅 Форматирование даты с часами и минутами
  function formatDateTime(isoString) {
    if (!isoString) return '-';
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  // 🔍 Проверка, является ли аренда просроченной
  function isOverdue(rental) {
    if (!rental || rental.status === 'completed') return false;
    const now = new Date();
    const endDate = new Date(rental.endDate);
    return now > endDate;
  }

  // 📊 Получение актуального статуса аренды
  function getActualStatus(rental) {
    if (!rental) return 'active';
    if (rental.status === 'completed') return 'completed';
    if (isOverdue(rental)) return 'overdue';
    return 'active';
  }

  // 🔄 Автоматическое обновление просроченных аренд
  useEffect(() => {
    const checkOverdue = async () => {
      const now = new Date();
      const updates = [];

      rentals.forEach((rental) => {
        if (rental.status === 'active') {
          const endDate = new Date(rental.endDate);
          if (now > endDate) {
            updates.push(rental._id);
          }
        }
      });

      if (updates.length > 0) {
        setRentals((prev) =>
          prev.map((rental) =>
            updates.includes(rental._id)
              ? { ...rental, status: 'overdue' }
              : rental
          )
        );

        for (const rentalId of updates) {
          try {
            await api.put(`/rentals/${rentalId}`, { status: 'overdue' });
          } catch (err) {
            console.error(`Ошибка обновления статуса аренды ${rentalId}:`, err);
          }
        }
      }
    };

    checkOverdue();
    const interval = setInterval(checkOverdue, 60000);
    return () => clearInterval(interval);
  }, [rentals, api, setRentals]);

  // 🎨 Функция для определения стиля строки в зависимости от статуса
  function getRowStyle(rental) {
    const actualStatus = getActualStatus(rental);
    if (actualStatus === 'overdue') {
      return 'bg-red-900/30 border-l-4 border-red-500';
    }
    if (actualStatus === 'active') {
      return 'bg-emerald-900/20 border-l-4 border-emerald-500';
    }
    if (actualStatus === 'completed') {
      return 'bg-blue-900/20 border-l-4 border-blue-500';
    }
    return '';
  }

  // 🎨 Функция для определения стиля карточки
  function getCardStyle(rental) {
    const actualStatus = getActualStatus(rental);
    if (actualStatus === 'overdue') {
      return 'bg-red-900/30 border-2 border-red-500';
    }
    if (actualStatus === 'active') {
      return 'bg-emerald-900/20 border-2 border-emerald-500';
    }
    if (actualStatus === 'completed') {
      return 'bg-blue-900/20 border-2 border-blue-500';
    }
    return 'bg-zinc-800';
  }

  // 🧮 Фильтрация и сортировка
  const filteredRentals = useMemo(() => {
    let result = rentals.map(rental => ({
      ...rental,
      actualStatus: getActualStatus(rental)
    }));

    if (filter) {
      result = result.filter((r) => r.actualStatus === filter);
    }

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.customerId?.fullName?.toLowerCase().includes(searchLower) ||
          r.carId?.name?.toLowerCase().includes(searchLower)
      );
    }

    result = [...result].sort((a, b) => {
      const dateA = new Date(a.createdAt || a._id);
      const dateB = new Date(b.createdAt || b._id);
      return dateB - dateA;
    });

    return result;
  }, [filter, search, rentals]);

  // 💰 Форматирование денег
  function formatMoney(value) {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  function parseMoney(value) {
    return parseFloat(String(value).replace(/\s/g, '').replace(',', '.')) || 0;
  }

  // 🚗 Обработка выбора машины (автоматическое заполнение цены)
  function handleCarSelect(carId, form) {
    setSelectedCarId(carId);
    const selectedCar = cars.find(c => c._id === carId);
    if (selectedCar && selectedCar.pricePerDay) {
      const formattedPrice = formatMoney(selectedCar.pricePerDay);
      setPriceInput(formattedPrice);
      // Пересчитываем итоговую сумму если уже выбраны даты
      if (form) {
        calculateTotal(form, selectedCar.pricePerDay);
      }
    }
  }

  // 📏 Автоматический расчёт суммы аренды
  function calculateTotal(form, priceValue = null) {
    if (!form) return;
    const start = new Date(form.startDate.value);
    const end = new Date(form.endDate.value);
    const price = priceValue ?? parseMoney(priceInput);

    if (!form.rentalType.value || isNaN(price) || isNaN(start) || isNaN(end)) return;

    let total = 0;
    if (form.rentalType.value === 'hourly') {
      const hours = Math.ceil((end - start) / (1000 * 60 * 60));
      total = hours * price;
    } else if (form.rentalType.value === 'daily') {
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      total = days * price;
    } else if (form.rentalType.value === 'monthly') {
      const months = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30));
      total = months * price;
    }

    setTotalInput(formatMoney(total));
  }

  // 🆕 Создание аренды
  async function handleAddRental(e) {
    e.preventDefault();
    const form = e.target;

    const startDate = toISOStringLocal(form.startDate.value);
    const endDate = toISOStringLocal(form.endDate.value);

    const data = {
      customerId: form.customerId.value,
      carId: form.carId.value,
      startDate: startDate,
      endDate: endDate,
      rentalType: form.rentalType.value,
      pricePerDay: parseMoney(priceInput),
      totalAmount: parseMoney(totalInput),
      status: 'active',
      createdAt: new Date().toISOString(),
    };

    try {
      const res = await api.post('/rentals', data);
      const newRental = {
        ...res.data,
        customerId: customers.find((c) => c._id === data.customerId) || res.data.customerId,
        carId: cars.find((c) => c._id === data.carId) || res.data.carId,
      };

      setRentals((prev) => [newRental, ...prev]);
      setShowModal(false);
      form.reset();
      setPriceInput('');
      setTotalInput('');
      setSelectedCarId('');
    } catch (err) {
      console.error('Ошибка при сохранении аренды:', err);
      alert('Не удалось сохранить аренду');
    }
  }

  // ✏️ Обновление аренды
  async function handleUpdateRental(e) {
    e.preventDefault();
    const form = e.target;
    if (!editedRental) return;

    const updated = {
      ...editedRental,
      customerId: form.customerId.value,
      carId: form.carId.value,
      startDate: toISOStringLocal(form.startDate.value),
      endDate: toISOStringLocal(form.endDate.value),
      rentalType: form.rentalType.value,
      pricePerDay: parseMoney(priceInput),
      totalAmount: parseMoney(totalInput),
    };

    try {
      const res = await api.put(`/rentals/${editedRental._id}`, updated);
      setRentals((prev) =>
        prev.map((r) =>
          r._id === editedRental._id
            ? {
              ...res.data,
              customerId: customers.find((c) => c._id === res.data.customerId) || res.data.customerId,
              carId: cars.find((c) => c._id === res.data.carId) || res.data.carId,
            }
            : r
        )
      );
      setEditMode(false);
      setShowDetails(false);
      setEditedRental(null);
      setPriceInput('');
      setTotalInput('');
      setSelectedCarId('');
    } catch (err) {
      console.error('Ошибка при обновлении аренды:', err);
      alert('Не удалось обновить аренду');
    }
  }

  // ✅ Завершение аренды
  async function handleCompleteRental(id) {
    try {
      await api.post(`/rentals/${id}/complete`);
      setRentals((prev) =>
        prev.map((r) => (r._id === id ? { ...r, status: 'completed' } : r))
      );
      setShowDetails(false);
    } catch (err) {
      console.error('Ошибка завершения аренды:', err);
      alert('Не удалось завершить аренду');
    }
  }

  function openNewRentalModal() {
    setEditMode(false);
    setEditedRental(null);
    setPriceInput('');
    setTotalInput('');
    setSelectedCarId('');
    setShowModal(true);
  }

  function openDetails(rental) {
    setSelectedRental(rental);
    setShowDetails(true);
  }

  function openEdit(rental) {
    const actualStatus = getActualStatus(rental);
    if (!rental || actualStatus !== 'active') return;

    const prepared = {
      ...rental,
      customerId: typeof rental.customerId === 'object' ? rental.customerId : { _id: rental.customerId },
      carId: typeof rental.carId === 'object' ? rental.carId : { _id: rental.carId },
    };
    setEditedRental(prepared);
    setPriceInput(formatMoney(rental.pricePerDay));
    setTotalInput(formatMoney(rental.totalAmount));
    setSelectedCarId(prepared.carId?._id || prepared.carId);
    setEditMode(true);
    setShowDetails(false);
  }

  // 🏷️ Получение текста статуса
  function getStatusText(status) {
    switch (status) {
      case 'active': return 'Активная';
      case 'completed': return 'Закрытая';
      case 'overdue': return 'Просроченная';
      default: return status;
    }
  }

  return (
    <div>
      {/* 🔍 Заголовок, поиск и фильтр */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
        <h1 className="text-xl font-semibold">Аренды</h1>
        <div className="flex flex-col sm:flex-row items-center gap-2">
          <input
            type="text"
            placeholder="Поиск по клиенту или машине"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-zinc-800 rounded px-3 py-2 w-full sm:w-64 focus:ring-2 focus:ring-emerald-500 focus:outline-none text-white"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-zinc-800 rounded px-3 py-2 w-full sm:w-auto"
          >
            <option value="">Все</option>
            <option value="active">Активные</option>
            <option value="completed">Завершенные</option>
            <option value="overdue">Просроченные</option>
          </select>
          <button
            onClick={openNewRentalModal}
            className="bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded w-full sm:w-auto"
          >
            Новая аренда
          </button>
        </div>
      </div>

      {/* 📋 Таблица (десктоп) */}
      <div className="bg-zinc-900 rounded overflow-x-auto">
        <table className="w-full text-left hidden md:table">
          <thead className="text-zinc-400">
            <tr>
              <th className="p-3">Клиент</th>
              <th className="p-3">Машина</th>
              <th className="p-3">Тип аренды</th>
              <th className="p-3">Сумма</th>
              <th className="p-3">Дата выдачи</th>
              <th className="p-3">Статус</th>
            </tr>
          </thead>
          <tbody>
            {filteredRentals.map((r) => (
              <tr
                key={r._id}
                className={`border-t border-zinc-800 hover:bg-zinc-800 cursor-pointer transition ${getRowStyle(r)}`}
                onClick={() => openDetails(r)}
              >
                <td className="p-3">{r.customerId?.fullName}</td>
                <td className="p-3">{r.carId?.name}</td>
                <td className="p-3 capitalize">
                  {r.rentalType === 'hourly'
                    ? 'Почасовая'
                    : r.rentalType === 'daily'
                      ? 'Дневная'
                      : 'Месячная'}
                </td>
                <td className="p-3">{formatMoney(r.totalAmount)}</td>
                <td className="p-3">{formatDateTime(r.createdAt)}</td>
                <td className="p-3">
                  <span className={`capitalize px-2 py-1 rounded text-sm ${r.actualStatus === 'active'
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : r.actualStatus === 'completed'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-red-500/20 text-red-400'
                    }`}>
                    {getStatusText(r.actualStatus)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Карточки для мобильных */}
        <div className="grid gap-4 p-4 md:hidden">
          {filteredRentals.map((r) => (
            <div
              key={r._id}
              className={`rounded-lg p-4 shadow cursor-pointer hover:opacity-90 transition ${getCardStyle(r)}`}
              onClick={() => openDetails(r)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white font-semibold">{r.customerId?.fullName}</p>
                  <p className="text-zinc-400">{r.carId?.name}</p>
                </div>
                <p className={`capitalize text-sm px-2 py-1 rounded ${r.actualStatus === 'active'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : r.actualStatus === 'completed'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-red-500/20 text-red-400'
                  }`}>
                  {getStatusText(r.actualStatus)}
                </p>
              </div>
              <div className="mt-2 flex justify-between">
                <p className="text-zinc-400">
                  {r.rentalType === 'hourly'
                    ? 'Почасовая'
                    : r.rentalType === 'daily'
                      ? 'Дневная'
                      : 'Месячная'}
                </p>
                <p className="text-emerald-400 font-semibold">{formatMoney(r.totalAmount)}</p>
              </div>
              <div className="mt-2 text-xs text-zinc-500">
                {formatDateTime(r.createdAt)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 📘 Детали аренды */}
      {showDetails && selectedRental && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 p-4 md:p-6 rounded-2xl md:rounded-3xl w-full md:w-auto md:min-w-[900px] md:max-w-5xl shadow-2xl border border-zinc-700/50 relative overflow-hidden max-h-[90vh] overflow-y-auto md:overflow-visible">
            <div className="hidden md:block absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl"></div>
            <div className="hidden md:block absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>

            <button
              onClick={() => setShowDetails(false)}
              className="absolute top-3 right-3 md:top-4 md:right-4 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full p-2 transition-all duration-200 z-10"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="relative mb-4 md:mb-5">
              <h2 className="text-xl md:text-2xl font-bold text-center bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent">
                Детали аренды
              </h2>
              <div className="h-1 w-16 md:w-20 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full mx-auto mt-2"></div>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="bg-zinc-800/50 rounded-lg md:rounded-xl p-3 border border-zinc-700/50 hover:border-emerald-500/30 transition-all duration-200">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 mb-0.5">Клиент</p>
                    <p className="text-sm md:text-base text-white font-semibold truncate">{selectedRental.customerId?.fullName}</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 rounded-lg md:rounded-xl p-3 border border-zinc-700/50 hover:border-blue-500/30 transition-all duration-200">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 mb-0.5">Машина</p>
                    <p className="text-sm md:text-base text-white font-semibold truncate">{selectedRental.carId?.name}</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 rounded-lg md:rounded-xl p-3 border border-zinc-700/50 hover:border-purple-500/30 transition-all duration-200">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 mb-0.5">Тип аренды</p>
                    <p className="text-sm md:text-base text-white font-semibold capitalize">
                      {selectedRental.rentalType === 'hourly'
                        ? 'Почасовая'
                        : selectedRental.rentalType === 'daily'
                          ? 'Дневная'
                          : 'Месячная'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-5">
              <div className="bg-zinc-800/50 rounded-lg md:rounded-xl p-3 border border-zinc-700/50 hover:border-orange-500/30 transition-all duration-200">
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 mb-1">Период аренды</p>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-zinc-400">От:</span>
                        <span className="text-xs text-white">{formatDateTime(selectedRental.startDate)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-zinc-400">До:</span>
                        <span className="text-xs text-white">{formatDateTime(selectedRental.endDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 rounded-lg md:rounded-xl p-3 border border-zinc-700/50 hover:border-cyan-500/30 transition-all duration-200">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 mb-0.5">Дата выдачи</p>
                    <p className="text-sm md:text-base text-white font-semibold">{formatDateTime(selectedRental.createdAt)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 rounded-lg md:rounded-xl p-3 border border-zinc-700/50 hover:border-pink-500/30 transition-all duration-200">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActualStatus(selectedRental) === 'active'
                      ? 'bg-emerald-500/10'
                      : getActualStatus(selectedRental) === 'completed'
                        ? 'bg-blue-500/10'
                        : 'bg-red-500/10'
                    }`}>
                    <svg className={`w-4 h-4 md:w-5 md:h-5 ${getActualStatus(selectedRental) === 'active'
                        ? 'text-emerald-400'
                        : getActualStatus(selectedRental) === 'completed'
                          ? 'text-blue-400'
                          : 'text-red-400'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-zinc-500 mb-0.5">Статус</p>
                    <span className={`inline-flex items-center px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-semibold ${getActualStatus(selectedRental) === 'active'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : getActualStatus(selectedRental) === 'completed'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                      {getStatusText(getActualStatus(selectedRental))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-xl md:rounded-2xl p-4 md:p-5 border border-emerald-500/20 mb-4 md:mb-5">
              <div className="grid grid-cols-2 gap-3 md:gap-6">
                <div className="text-center">
                  <p className="text-xs text-zinc-400 mb-1 md:mb-2">Цена за период</p>
                  <p className="text-lg md:text-2xl font-bold text-white">{formatMoney(selectedRental.pricePerDay)}</p>
                </div>
                <div className="text-center border-l border-zinc-700/50">
                  <p className="text-xs text-zinc-400 mb-1 md:mb-2">Итоговая сумма</p>
                  <p className="text-lg md:text-2xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-600 bg-clip-text text-transparent">
                    {formatMoney(selectedRental.totalAmount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="relative flex flex-wrap justify-end gap-2 md:gap-3">
              {getActualStatus(selectedRental) !== 'completed' && (
                <>
                  <button
                    className="px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 rounded-lg md:rounded-xl text-white text-sm md:text-base font-medium transition-all duration-200"
                    onClick={() => handleCompleteRental(selectedRental._id)}
                  >
                    <span className="flex items-center gap-1.5 md:gap-2">
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Завершить
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      const prepared = {
                        ...selectedRental,
                        customerId: typeof selectedRental.customerId === 'object' ? selectedRental.customerId : { _id: selectedRental.customerId },
                        carId: typeof selectedRental.carId === 'object' ? selectedRental.carId : { _id: selectedRental.carId },
                      };
                      setEditedRental(prepared);
                      setPriceInput(formatMoney(selectedRental.pricePerDay));
                      setTotalInput(formatMoney(selectedRental.totalAmount));
                      setSelectedCarId(prepared.carId?._id || prepared.carId);
                      setEditMode(true);
                      setShowDetails(false);
                    }}
                    className="px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg md:rounded-xl text-white text-sm md:text-base font-medium transition-all duration-200"
                  >
                    <span className="flex items-center gap-1.5 md:gap-2">
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Изменить
                    </span>
                  </button>
                </>
              )}
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg md:rounded-xl text-white text-sm md:text-base font-medium transition-all duration-200"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Новая аренда */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-[700px] sm:w-[90%] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800">
              <h2 className="text-lg md:text-xl font-semibold text-emerald-400">Новая аренда</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-zinc-500 hover:text-white text-xl md:text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddRental} className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Клиент</label>
                  <select
                    name="customerId"
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  >
                    <option value="">Выбрать клиента</option>
                    {customers.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.fullName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Машина</label>
                  <select
                    name="carId"
                    required
                    value={selectedCarId}
                    onChange={(e) => handleCarSelect(e.target.value, e.target.form)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  >
                    <option value="">Выбрать машину</option>
                    {cars.filter((c) => c.status === 'available').map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} - {formatMoney(c.pricePerDay)}/час
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Тип аренды</label>
                  <select
                    name="rentalType"
                    defaultValue="daily"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                    onChange={(e) => calculateTotal(e.target.form)}
                  >
                    <option value="daily">По дням</option>
                    <option value="hourly">Почасовая</option>
                    <option value="monthly">Помесечная</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">
                    Цена {selectedCarId && <span className="text-emerald-400">(автоматически)</span>}
                  </label>
                  <input
                    name="pricePerDay"
                    type="text"
                    placeholder="Цена"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value.replace(/[^\d.,]/g, ''))}
                    onBlur={(e) => {
                      const clean = e.target.value.replace(/\s/g, '').replace(',', '.');
                      if (!isNaN(clean) && clean !== '') {
                        const formatted = formatMoney(parseFloat(clean));
                        setPriceInput(formatted);
                        calculateTotal(e.target.form, parseFloat(clean));
                      }
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                  />
                </div>
              </div>
              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Начало</label>
                  <input
                    name="startDate"
                    type="datetime-local"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                    onChange={(e) => calculateTotal(e.target.form)}
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Конец</label>
                  <input
                    name="endDate"
                    type="datetime-local"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    required
                    onChange={(e) => calculateTotal(e.target.form)}
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Итого</label>
                  <input
                    name="totalAmount"
                    type="text"
                    value={totalInput}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-emerald-400 font-semibold text-sm md:text-base focus:outline-none"
                    readOnly
                  />
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 flex justify-end gap-2 md:gap-3 mt-4 md:mt-6 border-t border-zinc-800 pt-3 md:pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-3 md:px-4 py-1 md:py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm md:text-base transition"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-3 md:px-4 py-1 md:py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm md:text-base transition"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✏️ Редактировать аренду */}
      {editMode && editedRental && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-[700px] sm:w-[90%] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-zinc-800">
              <h2 className="text-lg md:text-xl font-semibold text-blue-400">Редактировать аренду</h2>
              <button
                onClick={() => {
                  setEditMode(false);
                  setEditedRental(null);
                }}
                className="text-zinc-500 hover:text-white text-xl md:text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateRental} className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Клиент</label>
                  <select
                    name="customerId"
                    defaultValue={editedRental.customerId?._id || editedRental.customerId}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {customers.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.fullName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Машина</label>
                  <select
                    name="carId"
                    value={selectedCarId}
                    onChange={(e) => handleCarSelect(e.target.value, e.target.form)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {cars.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} - {formatMoney(c.pricePerDay)}/час
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Тип аренды</label>
                  <select
                    name="rentalType"
                    defaultValue={editedRental.rentalType}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    onChange={(e) => calculateTotal(e.target.form)}
                  >
                    <option value="daily">По дням</option>
                    <option value="hourly">Почасовая</option>
                    <option value="monthly">Помесечная</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Цена</label>
                  <input
                    name="pricePerDay"
                    type="text"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value.replace(/[^\d.,]/g, ''))}
                    onBlur={(e) => {
                      const clean = e.target.value.replace(/\s/g, '').replace(',', '.');
                      if (!isNaN(clean) && clean !== '') {
                        const formatted = formatMoney(parseFloat(clean));
                        setPriceInput(formatted);
                        calculateTotal(e.target.form, parseFloat(clean));
                      }
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Начало</label>
                  <input
                    name="startDate"
                    type="datetime-local"
                    defaultValue={toLocalDatetimeString(editedRental.startDate)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    onChange={(e) => calculateTotal(e.target.form)}
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Конец</label>
                  <input
                    name="endDate"
                    type="datetime-local"
                    defaultValue={toLocalDatetimeString(editedRental.endDate)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-white text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    onChange={(e) => calculateTotal(e.target.form)}
                  />
                </div>
                <div>
                  <label className="block text-xs md:text-sm text-zinc-400 mb-1">Итого</label>
                  <input
                    name="totalAmount"
                    type="text"
                    value={totalInput}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 md:px-3 py-1 md:py-2 text-blue-400 font-semibold text-sm md:text-base focus:outline-none"
                    readOnly
                  />
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 flex justify-end gap-2 md:gap-3 mt-4 md:mt-6 border-t border-zinc-800 pt-3 md:pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditMode(false);
                    setEditedRental(null);
                  }}
                  className="px-3 md:px-4 py-1 md:py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm md:text-base transition"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-3 md:px-4 py-1 md:py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm md:text-base transition"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>  
      )}
    </div>
  );
}